import React, { useContext, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { RichText } from "prismic-reactjs";
import Script from "next/script";
import Head from "next/head";
import { globalPropsRequests } from "../../../utils/globalProps";
import Loader from "../../../components/Loader";
import { StorefrontContext } from "../../../providers/storefront";
import {
  getDocByType,
  getDocByTypeAndUid,
} from "../../../helpers/PrismicKnowledgeHub";
import PostPageSidebar from "../../../components/NewKnowledgeHub/PostPageSidebar";
import KnowledgeHubBreadcrumbs from "../../../components/NewKnowledgeHub/KnowledgeHubBreadcrumbs";
import Error from "../../../components/Error";
import KnowledgeHubHeader from "../../../components/KnowledgeHubHeader";
import Copyright from "../../../containers/Layout/Footer/Copyright";
import PostHeaderContent from "../../../components/NewKnowledgeHub/PostHeaderContent";
import PostsList from "../../../components/NewKnowledgeHub/PostsList";
import PostPageQuestion from "../../../components/NewKnowledgeHub/PostPageQuestion";

function PostPage({ content }) {
  const { openSearch, setOpenSearch, selectedDiscussion } =
    useContext(StorefrontContext);

  const router = useRouter();
  const isNotMainPage = router.asPath.split("/").length > 2;
  const { isFallback } = useRouter();

  // const [openMenu, setOpenMenu] = useState(false);

  const post = content.post;
  const discussions = content.discussions;

  const seoTitle = content?.post?.data?.seo_title;
  const seoDescription = content?.post?.data?.seo_description;
  const ogImage = content?.post?.data?.og_image;
  const ogDescription = content?.post?.data?.og_description;

  const [activeArticleIndex, setActiveArticleIndex] = useState(null);
  const [screenSize, setScreenSize] = useState(null);
  const [scrollTimeout, setScrollTimeout] = useState(null);

  if (isFallback) {
    return <Loader />;
  }

  if (!content) {
    return <Error code={404} />;
  }

  useEffect(() => {
    handleScrollToTop();
  }, []);

  useEffect(() => {
    function handleResize() {
      setScreenSize(window.innerWidth);
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [manualIndexSet, setManualIndexSet] = useState(false);

  const getDiscTitle = (discUid) => {
    const temp = discussions.find((disc) => disc.uid === discUid);
    return temp?.data.discussion_title;
  };

  const handleScrollToTop = () => {
    setActiveArticleIndex(0);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const preparedTitles = post.data?.body1.reduce((acc, block) => {
    const contentTitle = block.primary.content_title;

    const subTitles = block.items
      .filter((item) => item.set_to_menu === true && item.sub_title.length > 0)
      .map((item) => ({
        text: RichText.asText(item.sub_title),
        id: RichText.asText(item.sub_title).split(" ").join("-").toLowerCase(),
      }));

    if (
      contentTitle &&
      contentTitle[0] &&
      contentTitle[0].text &&
      contentTitle[0].text.length > 0
    ) {
      acc.push({
        text: RichText.asText(contentTitle),
        id: block.id,
        subTitles: [],
      });
    }

    if (subTitles.length > 0 && acc.length > 0) {
      acc[acc.length - 1].subTitles.push(...subTitles);
    }

    return acc;
  }, []);

  useEffect(() => {
    if (preparedTitles?.length > 0) {
      setActiveArticleIndex(preparedTitles[0]?.id);
    }
  }, [router.asPath]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      let newActiveIndex = preparedTitles[0]?.id;

      if (window.scrollY === 0) {
        setActiveArticleIndex(preparedTitles[0]?.id);
        return;
      }

      preparedTitles.forEach((title) => {
        const element = document.getElementById(title.id);
        if (element) {
          const offsetTop =
            element.getBoundingClientRect().top + window.scrollY;
          if (offsetTop <= scrollPosition) {
            newActiveIndex = title.id;
          }

          title.subTitles.forEach((subTitle) => {
            const subElement = document.getElementById(subTitle.id);
            if (subElement) {
              const subOffsetTop =
                subElement.getBoundingClientRect().top + window.scrollY;
              if (subOffsetTop <= scrollPosition) {
                newActiveIndex = subTitle.id;
              }
            }
          });
        }
      });

      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      const newTimeout = setTimeout(() => {
        if (newActiveIndex !== activeArticleIndex) {
          setActiveArticleIndex(newActiveIndex);
        }
      }, 200);

      setScrollTimeout(newTimeout);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [preparedTitles, activeArticleIndex, scrollTimeout]);

  const handleChangeArticle = (id) => {
    setManualIndexSet(true);
    setActiveArticleIndex(id);
  };

  const scrollToHeader = (blockId) => {
    const id = `${blockId}`;
    const element = document.getElementById(id);
    if (element) {
      const top = element.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({
        top,
        behavior: "smooth",
      });
    }
  };

  const handleScrollToElementAbove = (id, distance) => {
    const element = document.getElementById(id);
    if (element) {
      const { top } = element.getBoundingClientRect();
      window.scrollTo({
        top: window.scrollY + top - distance,
        behavior: "smooth",
      });
    }
  };

  // const scrollToHeader = (blockId) => {
  //   // const title = preparedTitles?.find((title) => title.id === blockId);
  //   // console.log('blockId',title);
  //   // if (!title) return;

  //   const id = `${blockId}`;
  //   if (blockId === preparedTitles[0].id) {
  //     handleScrollToTop();
  //   } else {
  //     if (screenSize >= 767) {
  //       handleScrollToElementAbove(id, 90);
  //     } else {
  //       handleScrollToElementAbove(id, 50);
  //     }
  //   }
  // };

  useEffect(() => {
    window.addEventListener("load", () => {
      scrollToHeader(activeArticleIndex);
    });
  }, [activeArticleIndex, router.asPath]);

  useEffect(() => {
    let timer;

    const handleScroll = () => {
      clearTimeout(timer);
      if (!manualIndexSet) {
        const elements = document.querySelectorAll("span[id]");
        const topScreen = window.scrollY;
        let firstVisiblePassed = false;

        elements.forEach((element) => {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + window.scrollY;

          if (elementTop > topScreen - 80 && !firstVisiblePassed) {
            setActiveArticleIndex(element.id);
            firstVisiblePassed = true;
          }
        });
      }
    };

    const handleManualIndexSetChange = () => {
      clearTimeout(timer);
      if (!manualIndexSet) {
        setManualIndexSet(true);
      }
      timer = setTimeout(() => {
        handleScroll();
      }, 500);
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("manualIndexSetChange", handleManualIndexSetChange);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener(
        "manualIndexSetChange",
        handleManualIndexSetChange
      );
    };
  }, [manualIndexSet]);

  useEffect(() => {
    const handleScrollReset = () => {
      if (manualIndexSet) {
        setManualIndexSet(false);
      }
    };

    window.addEventListener("scroll", handleScrollReset);

    return () => {
      window.removeEventListener("scroll", handleScrollReset);
    };
  }, [manualIndexSet, post]);

  // console.log('content', content);
  // console.log('topic ==>', topic);
  // console.log('discussions ==>', discussions);
  // console.log('discussion ==>', content.discussion);
  // console.log('post ==>', post);
  // console.log('posts ==>', posts);

  return (
    <>
      <Head>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:description" content={ogDescription} />
        <meta
          property="og:image"
          content={
            ogImage?.url
              ? ogImage?.url
              : "https://cdn.shopify.com/s/files/1/0522/3320/7988/files/ogImage.png?v=1713964496"
          }
        />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1024" />
      </Head>
      <Script
        src="https://static.cdn.prismic.io/prismic.js?new=true&repo=aftershock-knowledge-hub"
        strategy="lazyOnload"
      />
      <div className="body-wrapper">
        <KnowledgeHubHeader isNotMainPage={isNotMainPage} />
        <div className="background-wrapper">
          <div className="wrapper" onClick={() => setOpenSearch(false)}>
            {/* {!openSearch && (
              <div className="menu-button-wrapper">
                <div className="lamp-button" onClick={handleOpenMenu}>
                  <Image
                    draggable={false}
                    src="/icons/LampIcon.png"
                    alt="icon-search"
                    width={30}
                    height={30}
                    loading="lazy"
                  />
                </div>
              </div>
            )} */}
            <div className="container">
              <PostPageSidebar
                activeArticleIndex={activeArticleIndex}
                handleChangeArticle={handleChangeArticle}
                scrollToHeader={scrollToHeader}
                preparedTitles={preparedTitles}
              />
              <div className="content">
                <KnowledgeHubBreadcrumbs
                  topic={content.topic}
                  postPage={content.topic.data}
                  getDiscTitle={getDiscTitle}
                  post={post}
                  discussions={discussions}
                />
                <PostHeaderContent post={post} />
                <PostsList blocks={post.data?.body1} />
                <PostPageQuestion />
                <span className="back-top" onClick={handleScrollToTop}>
                  Back to top
                </span>
              </div>
            </div>
            {/* {openMenu && (
              <MobileSideBar
                post={post}
                activeArticleIndex={activeArticleIndex}
                handleChangeArticle={handleChangeArticle}
                handleCloseMenu={handleCloseMenu}
                scrollToHeader={scrollToHeader}
              />
            )} */}
          </div>
        </div>
        <div className="copyright-wrapper">
          <Copyright />
        </div>
      </div>
      <style jsx>{`
        .body-wrapper {
          width: 100%;
          min-height: 100vh;
        }
        .menu-button-wrapper {
          position: fixed;
          width: 100%;
          z-index: 5;
          background-color: transparent;
        }
        .lamp-button {
          position: absolute;
          display: none;
          top: -55px;
          left: 60%;
          z-index: 5;
          cursor: pointer;
        }
        @media (max-width: 575px) {
          .lamp-button {
            top: -45px;
            display: block;
          }
        }
        @media (max-width: 800px) {
          .lamp-button {
            display: block;
          }
        }
        .background-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
          background-color: #1a1a1a;
        }
        .wrapper {
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          padding: 0;
        }
        .container {
          width: 100%;
          height: 100%;
          display: grid;
          grid-template-columns: 25% 75%;
        }
        @media (max-width: 800px) {
          .container {
            display: flex;
            flex-direction: column;
            padding: 5px 15px;
          }
        }
        @media (max-width: 425px) {
          .container {
            padding: 5px 20px;
          }
        }
        @media (max-width: 375px) {
          .container {
            padding: 5px 12px;
          }
        }
        .content {
          width: 100%;
          display: flex;
          flex-direction: column;
          justify-content: start;
          align-items: stretch;
          padding: 10px 30px;
        }
        @media (max-width: 767px) {
          .content {
            padding: 0;
          }
        }
        .back-top {
          display: none;
        }
        @media (max-width: 767px) {
          .back-top {
            display: block;
            width: 100%;
            text-align: center;
            padding: 30px 0 20px;
            text-decoration: underline;
            color: #a41c1c;
            cursor: pointer;
            font-family: "Roboto-Medium";
            font-size: 10px;
          }
        }
      `}</style>
    </>
  );
}

export async function getStaticProps({ params, preview = false, previewData }) {
  const ref = previewData?.ref || null;
  const { topic, post } = params;

  const global = {};

  await Promise.all(globalPropsRequests)
    .then((responses) => {
      responses.forEach((res) => {
        if (res) {
          const [id, body] = res;
          global[id] = body.data;
        }
      });
    })
    .catch(() => null);

  const postData = await getDocByTypeAndUid("post", post, { ref });
  const topicData = await getDocByTypeAndUid("topic", topic, { ref });

  const discussions = await getDocByType("discussion", { ref });
  const posts = await getDocByType("post", { ref });

  const results = {
    post: {
      uid: post,
      data: postData?.data || null,
    },
    topic: {
      uid: topic,
      data: topicData?.data || null,
    },
    discussions: discussions,
    posts: posts,
  };

  return {
    props: {
      content: results || null,
      global,
      preview,
    },
    revalidate: 3600,
  };
}

export async function getStaticPaths() {
  const posts = await getDocByType("post");

  const paths = posts
    .filter((post) => post.data.topic && post.data.topic.uid)
    .map((post) => ({
      params: {
        topic: post.data.topic.uid,
        post: post.uid,
      },
    }));

  return {
    paths,
    fallback: "blocking",
  };
}

export default PostPage;
